import BaseView, { DomainModel3D } from './base-view';
import DrawerBoxView from './drawer-box-view';
import HandleView from './handle-view';
import logging from '../logging';
import pc from '../parametric-cache';
import { BufferGeometry, Group, Mesh, MeshStandardMaterial, Material, Box3, PlaneGeometry } from 'three';
import { Drawer } from '../domain';
import hitPlaneMaterial from '../view3d/hit-plane-material';
import visibleSize from '../view3d/visible-size';

const log = logging('view');

type XYZ = {x: number, y: number, z: number};
const set = (vec: XYZ, x: number, y: number, z: number) => {
    vec.x = x;
    vec.y = y;
    vec.z = z;
}


export default class DrawerView extends BaseView {

    declare model: Drawer;

    front: Group;
    panel: Mesh;
    frame?: Mesh;
    box: DrawerBoxView;
    slide: Group;

    handles: Array<HandleView>;

    hit: Mesh;

    constructor(model: DomainModel3D) {
        super(model);
        this.onReady.name = 'DrawerView';
        this.init();
    }

    make() {
        this.handles = [];
        const drawer = this.model;

        this.hit = new Mesh(new PlaneGeometry(), hitPlaneMaterial);
        this.hit.name = 'DrawerView hit rectangle';
        this.hit.visible = false;
        this.hit.userData.isSelectable = true;
        this.hit.userData.isMovable = true;
        this.add(this.hit);

        this.front = new Group();
        if(drawer.panel || this.isBoxOnly()) {
            this.panel = new Mesh(new BufferGeometry(), new MeshStandardMaterial());
            this.panel.castShadow = this.panel.receiveShadow = true;

            this.panel.name = 'Drawer Panel';
            //this.panel.userData.isSelectable = true;
            //this.panel.userData.isMovable = true;
            this.front.add(this.panel);
            this.highlights.add(this.panel);
        }

        if(drawer.frame) {
            this.frame = new Mesh(new BufferGeometry(), new Material());
	    this.frame.castShadow = this.frame.receiveShadow = true;

            this.frame.name = 'Drawer Frame';
            //this.frame.userData.isSelectable = true;
            //this.frame.userData.isMovable = true;
            this.front.add(this.frame);
            this.highlights.add(this.frame);
        }

        if(this.model.box) {
            this.box = new DrawerBoxView(this.model.box)
            this.onReady.depends(this.box.onReady);

            // This will re-parent the box's slide (the hardware that screws
            // to the panel) to this object without changing its world
            // transform, then duplicate and mirror it.
            this.box.onSlideAvailable.add((leftSide) => {
                this.attach(leftSide);
                leftSide.position.x = (drawer.width - drawer.box.width) / 2;

                const rightSide = leftSide.clone();
                this.add(rightSide);
                rightSide.scale.set(-1, 1, 1);
                rightSide.position.copy(leftSide.position);
                rightSide.position.x = drawer.width - leftSide.position.x;
            });

            this.box.onBoxAvailable.add(root => {
                root.traverse(obj => {
                    if(obj["isMesh"]) {
                        this.highlights.add(obj as Mesh);
                    }
                });
                this.updateHitSize();
            });
        }

        this.name = 'Drawer';

        this.slide = new Group()
        this.slide.name = 'Slide';

        this.add(this.slide);
        this.slide.add(this.box);
        this.slide.add(this.front);

        for(let handle of drawer.handles) {
            const view = new HandleView(handle);
            this.handles.push(view);
            this.slide.add(view);
            this.onReady.depends(view.onReady);
        }

        if(this.isBoxOnly()) {
            for(let child of this.slide.children) {
                child.position.z = -0.75;
            }
        }

        this.userData.isParametric = true;
    }

    bind() {

        if(this.isBoxOnly()) {
            this.bindMaterial(this.panel, this.model.box.materials[0]);
        } else if(this.panel) {
            this.bindMaterial(this.panel, this.model.panel.material);
        }
        if(this.frame) {
            this.bindMaterial(this.frame, this.model.frame.material);
        }

        this.model.bind('width', this.changeSize);
        this.model.on('height', this.changeSize);
        this.model.on('depth', this.changeSize);

        this.model.bind('open', () => {
            this.slide.position.z = this.model.open * this.model.depth;
        });

        this.model.bind('opacity', () => {
            const castShadow = this.model.opacity == 1;
            if(this.frame) this.frame.castShadow = castShadow;
            if(this.panel) this.panel.castShadow = castShadow;
            for(const handle of this.model.handles) {
                handle.opacity = this.model.opacity;
            }
        });
    }

    private changeSize = () => {
        const drawer = this.model;
        for(let model of this.model.handles) {
            // full width handles have width and height, other types ignore it
            model.width = this.model.width;
            model.height = this.model.height;
        }

        const box = this.box;
        if(drawer.box.modelAssetId) {
            box.position.x = drawer.width / 2;
            box.position.y = drawer.height / 2;
            box.position.z = 0;
        } else {
            box.position.x = (drawer.width - drawer.box.width) / 2;
            box.position.y = (drawer.height - drawer.box.height) / 2;
            // push it forward just a bit so it intersects the panel
            box.position.z = 0.0625 - drawer.box.depth;

            if(this.isBoxOnly()) {
                this.panel.position.x = box.position.x;
                this.panel.position.y = box.position.y;
            }
        }

        this.updates.queue(
            async () => { 
                let front: BufferGeometry;
                if(drawer.scoop) {
                    front = await pc.make(
                        'ScoopFront', drawer.box.width, drawer.box.height);
                } else if(drawer.panel == null || drawer.panel.style == null) {
                    front = await pc.make(
                        'FlatFront', drawer.box.width, drawer.box.height);
                } else {
                    front = await pc.make('FrameAndPanel',
                        (drawer.frame && drawer.frame.style)
                            ? drawer.frame.style
                            : null,
                        drawer.panel.style,
                        drawer.width, drawer.height,
                        drawer.panel.grain);

                }
                return front;
            },
            (frameAndPanel) => {
                try {
                    const [frameGeo, panelGeo] =
                        frameAndPanel as any as BufferGeometry[];
                    if(this.frame && frameGeo) {
                        this.frame.geometry = frameGeo;
                    }
                    if(this.panel) {
                        this.panel.geometry = panelGeo;
                    }


                    for(let handle of this.model.handles) {
                        if(handle.isExtrusionPull
                           || handle.style?.includes('pull-elite'))
                        {
                            let side = handle.side;
                            if(side == null) side = 'top';
                            switch(side) {
                                case "left":
                                    set(handle.position, 0, handle.height, 0);
                                    set(handle.scale, handle.height, 1, 1);
                                    set(handle.rotation, 0, 0, 1.5*Math.PI);
                                    break;
                                case "right":
                                    set(handle.position, handle.width, 0, 0);
                                    set(handle.scale, handle.height, 1, 1);
                                    set(handle.rotation, 0, 0, 0.5*Math.PI);
                                    break;
                                case "top":
                                    set(handle.position, handle.width, handle.height, 0);
                                    set(handle.scale, handle.width, 1, 1);
                                    set(handle.rotation, 0, 0, Math.PI);
                                    break;
                                case "bottom":
                                    set(handle.position, 0, 0, 0);
                                    set(handle.scale, handle.width, 1, 1);
                                    set(handle.rotation, 0, 0, 0);
                                    break;
                                default:
                                    log.error(`Unsupported full-width obj side: ${handle.side}`);
                            }
                        } else {
                            handle.position.x = this.model.width / 2;
                            // This places the handle 4" below the top, or in
                            // the middle if the drawer is shorter than 8".
                            handle.position.y = Math.max(
                                this.model.height / 2,
                                this.model.height - 4);
                            if(handle.assetId) {
                                handle.position.z = 0.75;
                            }
                        }
                    }
                } finally {
                    this.updateHitSize();
                    this.onReady.trigger();
                }
            }
        );
    }

    isBoxOnly() {
        if(this.model.scoop) return true;
        if(this.model.box && this.model.box.modelAssetId) return false;
        if(this.model.panel == null || this.model.panel.style == null) return true;

        return false;
    }

    updateHitSize() {
        this.hit.scale.set(this.model.width, this.model.height, 1);
        this.hit.position.set(this.model.width/2, this.model.height/2, 0);
    }
}
